<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Kritik Stok</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Ürün ara"
        single-line
        hide-details
        clearable
        style="max-width: 260px"
      ></v-text-field>
      <v-btn icon class="ml-2" :loading="loading" @click="load">
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn color="primary" class="ml-2" :loading="saving" :disabled="!isDirty" @click="save">
        Kaydet
      </v-btn>
    </v-toolbar>

    <v-layout wrap class="my-2 px-2">
      <v-flex xs6 sm3>
        <v-card flat class="pa-2 text-xs-center elevation-1">
          <div class="caption grey--text">Stok yok</div>
          <div class="title red--text">{{ counts.out }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pl-2">
        <v-card flat class="pa-2 text-xs-center elevation-1">
          <div class="caption grey--text">Kritik stok altı</div>
          <div class="title amber--text text--darken-3">{{ counts.below }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pl-2">
        <v-card flat class="pa-2 text-xs-center elevation-1">
          <div class="caption grey--text">Yeterli</div>
          <div class="title green--text">{{ counts.ok }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pl-2">
        <v-card flat class="pa-2 text-xs-center elevation-1">
          <div class="caption grey--text">Takip ettiğim ürün</div>
          <div class="title">{{ counts.tracked }}</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout wrap class="px-2 mb-2" align-center>
      <v-checkbox
        v-model="onlyTracked"
        label="Sadece kritik stok girdiklerim"
        hide-details
        class="mt-0 mr-4"
      ></v-checkbox>
      <v-checkbox
        v-model="onlyWarnings"
        label="Sadece uyarılı ürünler"
        hide-details
        class="mt-0"
      ></v-checkbox>
    </v-layout>

    <v-alert :value="true" type="info" outline class="mx-2 caption">
      Kritik stok <strong>0</strong> ise uyarı verilmez. 0'dan büyük girdiğinizde, kasanızdaki
      malzemeyle üründen kaç adet yapabileceğiniz hesaplanır; reçetedeki en dar kalem sınırı belirler.
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="filteredLines"
      :loading="loading"
      :rows-per-page-items="[50, 100, 500, { text: 'Tümü', value: -1 }]"
      item-key="productId"
      expand
      class="elevation-1 mx-2"
    >
      <template v-slot:items="props">
        <tr :class="rowClass(props.item)" @click="props.expanded = !props.expanded" style="cursor: pointer">
          <td class="px-2">{{ props.item.productSku || '-' }}</td>
          <td class="px-2">{{ props.item.productName }}</td>
          <td class="px-2" style="max-width: 120px" @click.stop>
            <v-text-field
              v-model.number="drafts[props.item.productId]"
              type="number"
              min="0"
              hide-details
              single-line
            ></v-text-field>
          </td>
          <td class="px-2 text-xs-right">{{ props.item.hasRecipe ? props.item.buildableQuantity : '-' }}</td>
          <td class="px-2">
            <v-chip v-if="props.item.status" small :color="statusColor(props.item.status)" text-color="white">
              {{ props.item.statusName }}
            </v-chip>
            <span v-else class="grey--text">-</span>
          </td>
          <td class="px-2 text-xs-right">
            <v-icon small>{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon>
          </td>
        </tr>
      </template>

      <template v-slot:expand="props">
        <v-card flat class="pa-2 grey lighten-4">
          <div v-if="!props.item.hasRecipe" class="caption grey--text px-2 py-1">
            Bu ürünün reçetesi tanımlı değil — kasadan yapılabilir adet hesaplanamıyor.
          </div>
          <table v-else class="critical-components">
            <thead>
              <tr>
                <th>Alt kalem</th>
                <th class="text-xs-right">Gereken (1 ürün için)</th>
                <th class="text-xs-right">Kasamdaki</th>
                <th class="text-xs-right">Bu kalemden çıkan</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in props.item.components" :key="c.inventoryItemId">
                <td>
                  {{ c.inventoryItemLabel }}
                  <v-chip v-if="c.isBottleneck" small color="deep-orange" text-color="white" class="ml-1">
                    sınırlayan
                  </v-chip>
                </td>
                <td class="text-xs-right">{{ c.requiredQuantity }}</td>
                <td class="text-xs-right">{{ c.heldQuantity }}</td>
                <td class="text-xs-right">{{ c.buildableQuantity }}</td>
              </tr>
            </tbody>
          </table>
        </v-card>
      </template>

      <template v-slot:no-data>
        <div class="text-xs-center pa-4 grey--text">Gösterilecek ürün yok.</div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  data: () => ({
    loading: false,
    saving: false,
    lines: [],
    drafts: {},
    search: "",
    onlyTracked: false,
    onlyWarnings: false,
    headers: [
      { text: "SKU", value: "productSku" },
      { text: "Ürün", value: "productName" },
      { text: "Kritik Stok", value: "criticalQuantity", sortable: false },
      { text: "Kasamdan Yapılabilir", value: "buildableQuantity", align: "right" },
      { text: "Durum", value: "status" },
      { text: "", value: "", sortable: false, align: "right" },
    ],
  }),

  computed: {
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      return this.lines.filter((l) => {
        if (this.onlyWarnings && l.status !== 2 && l.status !== 3) return false;
        if (this.onlyTracked && !(this.drafts[l.productId] > 0)) return false;
        if (!q) return true;
        return [l.productName, l.productSku]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q));
      });
    },
    counts() {
      return {
        out: this.lines.filter((l) => l.status === 3).length,
        below: this.lines.filter((l) => l.status === 2).length,
        ok: this.lines.filter((l) => l.status === 1).length,
        tracked: this.lines.filter((l) => l.criticalQuantity > 0).length,
      };
    },
    isDirty() {
      return this.lines.some((l) => this.normalize(this.drafts[l.productId]) !== l.criticalQuantity);
    },
  },

  created() {
    this.load();
  },

  methods: {
    normalize(v) {
      const n = parseInt(v, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    },
    statusColor(status) {
      if (status === 3) return "red";
      if (status === 2) return "amber darken-2";
      if (status === 1) return "green";
      return "grey";
    },
    rowClass(item) {
      if (item.status === 3) return "row-out";
      if (item.status === 2) return "row-below";
      return "";
    },
    async load() {
      this.loading = true;
      try {
        this.lines = (await this.$store.dispatch("criticalStock/getMyCriticalStock")) || [];
        const drafts = {};
        this.lines.forEach((l) => { drafts[l.productId] = l.criticalQuantity; });
        this.drafts = drafts;
      } catch (e) {
        this.showError("Kritik stok listesi yüklenemedi", e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      const items = this.lines
        .filter((l) => this.normalize(this.drafts[l.productId]) !== l.criticalQuantity)
        .map((l) => ({ productId: l.productId, criticalQuantity: this.normalize(this.drafts[l.productId]) }));

      if (!items.length) return;

      this.saving = true;
      try {
        await this.$store.dispatch("criticalStock/saveMyCriticalStock", items);
        await this.load();
      } catch (e) {
        this.showError("Kritik stok kaydedilemedi", e);
      } finally {
        this.saving = false;
      }
    },
    showError(prefix, e) {
      this.$store.state.dialog = true;
      this.$store.state.message =
        prefix + ": " +
        ((e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "");
    },
  },
};
</script>

<style scoped>
.row-below { background-color: #fff8e1; }
.row-out { background-color: #ffebee; }
.critical-components { width: 100%; border-collapse: collapse; }
.critical-components th,
.critical-components td { padding: 4px 8px; font-size: 12px; border-bottom: 1px solid #e0e0e0; }
.critical-components th { color: rgba(0, 0, 0, 0.54); font-weight: 500; text-align: left; }
</style>
